/**
 * Fonctions utilitaires pour SkyChart
 */

/**
 * Convertit les degrés en radians
 * @param {number} deg - Angle en degrés
 * @returns {number} Angle en radians
 */
export function degToRad(deg) {
    return (deg * Math.PI) / 180;
}

/**
 * The drawing angle of an ecliptic longitude on a Western wheel: the Ascendant at nine o'clock and
 * the zodiac running counter-clockwise, as every ephemeris draws it. Points are placed with
 * (cx + r·cos, cy − r·sin). The wheel used to write `90 − longitude`, which put 0° Aries at the top
 * and ran clockwise: east and west swapped against any printed chart.
 * @param {number} longitude - ecliptic longitude, in degrees
 * @param {number} [ascendant=0] - the Ascendant's longitude; 0 puts 0° Aries at nine o'clock
 * @returns {number} Angle in radians
 */
export function wheelAngle(longitude, ascendant = 0) {
    return degToRad(180 + longitude - ascendant);
}

/**
 * Convertit un chiffre romain en nombre arabe
 * @param {string} roman - Chiffre romain
 * @returns {number} Nombre arabe correspondant
 */
export function romanToArabic(roman) {
    switch (roman) {
        case 'I': return 1;
        case 'II': return 2;
        case 'III': return 3;
        case 'IV': return 4;
        case 'V': return 5;
        case 'VI': return 6;
        case 'VII': return 7;
        case 'VIII': return 8;
        case 'IX': return 9;
        case 'X': return 10;
        case 'XI': return 11;
        case 'XII': return 12;
        default: return parseInt(roman, 10);
    }
}

/**
 * Convertit un nombre arabe en chiffre romain
 * @param {number} num - Nombre arabe
 * @returns {string} Chiffre romain correspondant
 */
export function arabicToRoman(num) {
    switch (num) {
        case 1: return 'I';
        case 2: return 'II';
        case 3: return 'III';
        case 4: return 'IV';
        case 5: return 'V';
        case 6: return 'VI';
        case 7: return 'VII';
        case 8: return 'VIII';
        case 9: return 'IX';
        case 10: return 'X';
        case 11: return 'XI';
        case 12: return 'XII';
        default: return num.toString();
    }
}

/**
 * Ajuste l'opacité d'une couleur
 * @param {string} color - Couleur au format hex (#RRGGBB) ou rgb(r,g,b)
 * @param {number} opacity - Opacité entre 0 et 1
 * @returns {string} Couleur avec opacité ajustée au format rgba
 */
export function adjustColorOpacity(color, opacity) {
    // Si la couleur est au format hex (#RRGGBB)
    if (color.startsWith('#')) {
        // Convertir en RGB
        const r = parseInt(color.substr(1, 2), 16);
        const g = parseInt(color.substr(3, 2), 16);
        const b = parseInt(color.substr(5, 2), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    // Si la couleur est déjà au format rgba
    else if (color.startsWith('rgba')) {
        return color.replace(/rgba\((.+),\s*[\d.]+\)/, `rgba($1, ${opacity})`);
    }
    // Si la couleur est au format rgb
    else if (color.startsWith('rgb')) {
        return color.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
    }
    return color;
}

/**
 * Détermine si une couleur est claire ou foncée
 * @param {string} color - Couleur au format hex (#RRGGBB) ou rgb(r,g,b)
 * @returns {boolean} true si la couleur est claire, false sinon
 */
export function isLightColor(color) {
    // Convertir en RGB si nécessaire
    let r, g, b;

    if (color.startsWith('#')) {
        r = parseInt(color.substr(1, 2), 16);
        g = parseInt(color.substr(3, 2), 16);
        b = parseInt(color.substr(5, 2), 16);
    } else if (color.startsWith('rgb')) {
        const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        if (rgbMatch) {
            r = parseInt(rgbMatch[1], 10);
            g = parseInt(rgbMatch[2], 10);
            b = parseInt(rgbMatch[3], 10);
        } else {
            return true; // Par défaut, considérer comme claire
        }
    } else {
        return true; // Par défaut, considérer comme claire
    }

    // Formule pour calculer la luminosité
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5;
}